import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Clock, Users, Award } from 'lucide-react';
import { usePagination } from '../hooks/usePagination';
import PaginationControls from '../components/PaginationControls';
import DentalSlideshowBackground from '../components/DentalSlideshowBackground';

interface College {
  name: string;
  location: string;
  duration: string;
  established: string;
  cutoff: string;
  university: string;
  type: string;
  notes?: string;
}

const BDSPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('Deemed University');
  const [searchTerm, setSearchTerm] = useState('');

  const categories = ['Government', 'Private', 'Deemed University'];

  const colleges: Record<string, College[]> = {
    Government: [
      {
        name: "Government Dental College & Hospital, Cuddalore",
        location: "Cuddalore, Tamil Nadu",
        duration: "5 years",
        established: "1980",
        cutoff: "NEET: 95+ percentile",
        university: "The Tamil Nadu Dr. MGR Medical University, Chennai",
        type: "Government",
        notes: "DCI recommended to GOI to reduce 13 BDS seats under EWS category from the AY 2024-25"
      },
      {
        name: "Tamil Nadu Government Dental College & Hospital, Chennai",
        location: "Chennai, Tamil Nadu",
        duration: "5 years",
        established: "1952",
        cutoff: "NEET: 97+ percentile",
        university: "The Tamil Nadu Dr. MGR Medical University, Chennai",
        type: "Government"
      }
    ],
    Private: [
      {
        name: "Adhiparasakthi Dental College & Hospital, Melmaruvathur",
        location: "Melmaruvathur, Tamil Nadu",
        duration: "5 years",
        established: "2005",
        cutoff: "NEET: 85+ percentile",
        university: "The Tamil Nadu Dr. MGR Medical University, Chennai",
        type: "Private"
      },
      {
        name: "Best Dental Science College, Madurai",
        location: "Madurai, Tamil Nadu",
        duration: "5 years",
        established: "2007",
        cutoff: "NEET: 82+ percentile",
        university: "The Tamil Nadu Dr. MGR Medical University, Chennai",
        type: "Private"
      },
      {
        name: "Chettinad Dental College & Research Institute, Kancheepuram",
        location: "Kancheepuram, Tamil Nadu",
        duration: "5 years",
        established: "2007",
        cutoff: "NEET: 88+ percentile",
        university: "The Tamil Nadu Dr. MGR Medical University, Chennai",
        type: "Private"
      },
      {
        name: "CSI College of Dental Sciences & Research, Madurai",
        location: "Madurai, Tamil Nadu",
        duration: "5 years",
        established: "2007",
        cutoff: "NEET: 80+ percentile",
        university: "The Tamil Nadu Dr. MGR Medical University, Chennai",
        type: "Private"
      },
      {
        name: "JKK Natrajah Dental College & Hospital, Komarapalayam",
        location: "Komarapalayam, Tamil Nadu",
        duration: "5 years",
        established: "1987",
        cutoff: "NEET: 78+ percentile",
        university: "The Tamil Nadu Dr. MGR Medical University, Chennai",
        type: "Private"
      },
      {
        name: "K.S.R. Institute of Dental Science & Research, Tiruchengode",
        location: "Tiruchengode, Tamil Nadu",
        duration: "5 years",
        established: "2004",
        cutoff: "NEET: 81+ percentile",
        university: "The Tamil Nadu Dr. MGR Medical University, Chennai",
        type: "Private"
      },
      {
        name: "Karpaga Vinayaga Institute of Dental Sciences, Kanchipuram",
        location: "Kanchipuram, Tamil Nadu",
        duration: "5 years",
        established: "2007",
        cutoff: "NEET: 84+ percentile",
        university: "The Tamil Nadu Dr. MGR Medical University, Chennai",
        type: "Private"
      },
      {
        name: "Madha Dental College & Hospital, Kundrathur",
        location: "Kundrathur, Tamil Nadu",
        duration: "5 years",
        established: "2007",
        cutoff: "NEET: 83+ percentile",
        university: "The Tamil Nadu Dr. MGR Medical University, Chennai",
        type: "Private"
      },
      {
        name: "Meenakshi Ammal Dental College & Hospital, Chennai",
        location: "Chennai, Tamil Nadu",
        duration: "5 years",
        established: "1990",
        cutoff: "NEET: 89+ percentile",
        university: "The Tamil Nadu Dr. MGR Medical University, Chennai",
        type: "Private"
      },
      {
        name: "Priyadarshini Dental College & Hospital, Pandur",
        location: "Pandur, Tamil Nadu",
        duration: "5 years",
        established: "2007",
        cutoff: "NEET: 82+ percentile",
        university: "The Tamil Nadu Dr. MGR Medical University, Chennai",
        type: "Private"
      },
      {
        name: "R.V.S. Dental College & Hospital, Sulur",
        location: "Sulur, Tamil Nadu",
        duration: "5 years",
        established: "2008",
        cutoff: "NEET: 80+ percentile",
        university: "The Tamil Nadu Dr. MGR Medical University, Chennai",
        type: "Private"
      },
      {
        name: "Ragas Dental College & Hospital, Chennai",
        location: "Chennai, Tamil Nadu",
        duration: "5 years",
        established: "1988",
        cutoff: "NEET: 91+ percentile",
        university: "The Tamil Nadu Dr. MGR Medical University, Chennai",
        type: "Private",
        notes: "GOI reduced BDS seats from 100 to 50 for AY 2024-25"
      },
      {
        name: "Rajas Dental College & Hospital, Kavalkinaru Jn",
        location: "Kavalkinaru, Tamil Nadu",
        duration: "5 years",
        established: "1987",
        cutoff: "NEET: 77+ percentile",
        university: "The Tamil Nadu Dr. MGR Medical University, Chennai",
        type: "Private",
        notes: "Admission allowed as per High Court order dated 9.11.2021"
      },
      {
        name: "S.R.M. Dental College, Ramapuram, Chennai",
        location: "Chennai, Tamil Nadu",
        duration: "5 years",
        established: "1996",
        cutoff: "NEET: 87+ percentile",
        university: "The Tamil Nadu Dr. MGR Medical University, Chennai",
        type: "Private"
      },
      {
        name: "Sathyabama University Dental College and Hospital, Chennai",
        location: "Chennai, Tamil Nadu",
        duration: "5 years",
        established: "2009",
        cutoff: "NEET: 85+ percentile",
        university: "Sathyabama Institute of Science and Technology",
        type: "Private"
      },
      {
        name: "Saveetha Dental College & Hospital, Chennai",
        location: "Chennai, Tamil Nadu",
        duration: "5 years",
        established: "1988",
        cutoff: "NEET: 92+ percentile",
        university: "Saveetha Institute of Medical and Technical Sciences",
        type: "Private"
      },
      {
        name: "Sree Balaji Dental College & Hospital, Narayanpuram",
        location: "Chennai, Tamil Nadu",
        duration: "5 years",
        established: "1989",
        cutoff: "NEET: 86+ percentile",
        university: "Bharath Institute of Higher Education and Research",
        type: "Private"
      },
      {
        name: "Sree Mookambika Institute of Dental Sciences, Kulasekharam",
        location: "Kulasekharam, Tamil Nadu",
        duration: "5 years",
        established: "1999",
        cutoff: "NEET: 79+ percentile",
        university: "The Tamil Nadu Dr. MGR Medical University, Chennai",
        type: "Private"
      },
      {
        name: "Sri Ramachandra Dental College & Hospital, Porur",
        location: "Chennai, Tamil Nadu",
        duration: "5 years",
        established: "1995",
        cutoff: "NEET: 94+ percentile",
        university: "Sri Ramachandra Institute of Higher Education and Research",
        type: "Private"
      },
      {
        name: "Sri Ramakrishna Dental College & Hospital, Coimbatore",
        location: "Coimbatore, Tamil Nadu",
        duration: "5 years",
        established: "2000",
        cutoff: "NEET: 84+ percentile",
        university: "The Tamil Nadu Dr. MGR Medical University, Chennai",
        type: "Private",
        notes: "Admission allowed. However, GOI decision is awaited."
      },
      {
        name: "Sri Venkateswara Dental College & Hospital, Kancheepuram",
        location: "Kancheepuram, Tamil Nadu",
        duration: "5 years",
        established: "2007",
        cutoff: "NEET: 83+ percentile",
        university: "The Tamil Nadu Dr. MGR Medical University, Chennai",
        type: "Private"
      },
      {
        name: "SRM Kattankulathur Dental College & Hospital, Kanchipuram",
        location: "Kanchipuram, Tamil Nadu",
        duration: "5 years",
        established: "2007",
        cutoff: "NEET: 88+ percentile",
        university: "SRM Institute of Science and Technology",
        type: "Private"
      },
      {
        name: "Tagore Dental College & Hospital, Chennai",
        location: "Chennai, Tamil Nadu",
        duration: "5 years",
        established: "2007",
        cutoff: "NEET: 85+ percentile",
        university: "The Tamil Nadu Dr. MGR Medical University, Chennai",
        type: "Private"
      },
      {
        name: "Thai Moogambigai Dental College & Hospital, Chennai",
        location: "Chennai, Tamil Nadu",
        duration: "5 years",
        established: "1991",
        cutoff: "NEET: 86+ percentile",
        university: "The Tamil Nadu Dr. MGR Medical University, Chennai",
        type: "Private"
      },
      {
        name: "Vinayaka Mission's Sankarachariyar Dental College, Salem",
        location: "Salem, Tamil Nadu",
        duration: "5 years",
        established: "1986",
        cutoff: "NEET: 82+ percentile",
        university: "Vinayaka Mission's Research Foundation",
        type: "Private"
      },
      {
        name: "Vivekanandha Dental College for Women, Elayampalayam",
        location: "Elayampalayam, Tamil Nadu",
        duration: "5 years",
        established: "2007",
        cutoff: "NEET: 83+ percentile",
        university: "The Tamil Nadu Dr. MGR Medical University, Chennai",
        type: "Private"
      }
    ],
    'Deemed University': [
      {
        name: "Meenakshi Ammal Dental College & Hospital, Chennai",
        location: "Chennai, Tamil Nadu",
        duration: "5 years",
        established: "1990",
        cutoff: "NEET: 89+ percentile",
        university: "Meenakshi Academy of Higher Education and Research",
        type: "Deemed University"
      },
      {
        name: "S.R.M. Dental College, Ramapuram, Chennai",
        location: "Chennai, Tamil Nadu",
        duration: "5 years",
        established: "1996",
        cutoff: "NEET: 87+ percentile",
        university: "SRM Institute of Science and Technology",
        type: "Deemed University"
      },
      {
        name: "Sathyabama University Dental College and Hospital, Chennai",
        location: "Chennai, Tamil Nadu",
        duration: "5 years",
        established: "2009",
        cutoff: "NEET: 85+ percentile",
        university: "Sathyabama Institute of Science and Technology",
        type: "Deemed University"
      },
      {
        name: "Saveetha Dental College & Hospital, Chennai",
        location: "Chennai, Tamil Nadu",
        duration: "5 years",
        established: "1988",
        cutoff: "NEET: 92+ percentile",
        university: "Saveetha Institute of Medical and Technical Sciences",
        type: "Deemed University"
      },
      {
        name: "Sree Balaji Dental College & Hospital, Narayanpuram",
        location: "Chennai, Tamil Nadu",
        duration: "5 years",
        established: "1989",
        cutoff: "NEET: 86+ percentile",
        university: "Bharath Institute of Higher Education and Research",
        type: "Deemed University"
      },
      {
        name: "Sri Ramachandra Dental College & Hospital, Porur",
        location: "Chennai, Tamil Nadu",
        duration: "5 years",
        established: "1995",
        cutoff: "NEET: 94+ percentile",
        university: "Sri Ramachandra Institute of Higher Education and Research",
        type: "Deemed University"
      },
      {
        name: "SRM Kattankulathur Dental College & Hospital, Kanchipuram",
        location: "Kanchipuram, Tamil Nadu",
        duration: "5 years",
        established: "2007",
        cutoff: "NEET: 88+ percentile",
        university: "SRM Institute of Science and Technology",
        type: "Deemed University"
      },
      {
        name: "Thai Moogambigai Dental College & Hospital, Chennai",
        location: "Chennai, Tamil Nadu",
        duration: "5 years",
        established: "1991",
        cutoff: "NEET: 86+ percentile",
        university: "Dr. M.G.R. Educational and Research Institute",
        type: "Deemed University"
      },
      {
        name: "Vinayaka Mission's Sankarachariyar Dental College, Salem",
        location: "Salem, Tamil Nadu",
        duration: "5 years",
        established: "1986",
        cutoff: "NEET: 82+ percentile",
        university: "Vinayaka Mission's Research Foundation",
        type: "Deemed University"
      }
    ]
  };

  const filteredColleges = colleges[selectedCategory]?.filter(college =>
    college.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    college.location.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  // Pagination using custom hook
  const pagination = usePagination({
    data: filteredColleges,
    initialItemsPerPage: 25
  });

  // Reset pagination when category or search changes
  React.useEffect(() => {
    pagination.resetPagination();
  }, [selectedCategory, searchTerm, pagination]);

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  const TableView = () => (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">College</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cutoff</th>
              {/* Removed Action column */}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {pagination.currentItems.map((college, index) => (
              <motion.tr
                key={index}
                className="hover:bg-gray-50"
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                transition={{ delay: index * 0.1 }}
              >
                <td className="px-6 py-4">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{college.name}</div>
                    <div className="text-sm text-gray-500">Est. {college.established}</div>
                    {college.notes && (
                      <div className="text-xs text-orange-600 mt-1">{college.notes}</div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{college.location}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    college.type === 'Government' 
                      ? 'bg-green-100 text-green-800' 
                      : college.type === 'Deemed University'
                      ? 'bg-purple-100 text-purple-800'
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {college.type}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{college.cutoff}</td>
                {/* Removed Action cell */}
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-black flex flex-col items-center justify-start py-8 px-2 relative overflow-hidden">
      <div className="absolute inset-0 w-full h-full z-0">
        <DentalSlideshowBackground />
      </div>
      <div className="relative z-10 w-full">
        {/* Hero Section */}
        <div className="text-center py-16">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 drop-shadow-glow mb-6 animate-glow">
            BDS Dental Colleges in Tamil Nadu
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-blue-100 max-w-3xl mx-auto">
            Explore top dental colleges offering Bachelor of Dental Surgery (BDS) programs with comprehensive information about admission requirements and cutoff details.
          </p>
        </div>

        {/* Search and Filter Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* ...existing code... */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-xl shadow-lg p-6 mb-8"
          >
            {/* ...existing code... */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              {/* ...existing code... */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder="Search colleges by name or location..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              {/* ...existing code... */}
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-6 py-3 rounded-lg font-medium transition-colors duration-200 ${
                      selectedCategory === category
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Results Summary */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mb-6"
          >
            <p className="text-gray-600">
              Showing <span className="font-semibold text-blue-600">{pagination.startIndex + 1}-{Math.min(pagination.endIndex, filteredColleges.length)}</span> of <span className="font-semibold text-blue-600">{filteredColleges.length}</span> dental colleges in <span className="font-semibold">{selectedCategory}</span> category
              {pagination.totalPages > 1 && (
                <span className="ml-2 text-sm text-gray-500">(Page {pagination.currentPage} of {pagination.totalPages})</span>
              )}
            </p>
          </motion.div>

          {/* College Table */}
          <TableView />

          {/* Pagination Controls */}
          {filteredColleges.length > 0 && (
            <PaginationControls
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              itemsPerPage={pagination.itemsPerPage}
              totalItems={filteredColleges.length}
              startIndex={pagination.startIndex}
              endIndex={pagination.endIndex}
              onNextPage={pagination.handleNextPage}
              onPrevPage={pagination.handlePrevPage}
              onItemsPerPageChange={pagination.setItemsPerPage}
              onPageChange={pagination.goToPage}
              className="mt-8"
            />
          )}

          {filteredColleges.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12"
            >
              <div className="text-gray-500 text-lg">No colleges found matching your criteria.</div>
            </motion.div>
          )}
        </div>

        {/* Information Section */}
        <div className="bg-white py-16">
          {/* ...existing code... */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-4">About BDS Program</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Bachelor of Dental Surgery (BDS) is a 5-year undergraduate program that prepares students for a career in dentistry. 
                The program includes both theoretical knowledge and practical training in dental care and oral health.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* ...existing code... */}
              {[
                {
                  icon: Clock,
                  title: "Duration",
                  description: "5 years including internship",
                  color: "blue"
                },
                {
                  icon: Award,
                  title: "Eligibility",
                  description: "12th with Physics, Chemistry, Biology",
                  color: "green"
                },
                {
                  icon: Users,
                  title: "Entrance Exam",
                  description: "NEET (National Eligibility Entrance Test)",
                  color: "purple"
                },
                {
                  icon: Users,
                  title: "Career Scope",
                  description: "Private practice, hospitals, research",
                  color: "orange"
                }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 * index }}
                  className="text-center"
                >
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-full bg-${item.color}-100 flex items-center justify-center`}>
                    <item.icon className={`h-8 w-8 text-${item.color}-600`} />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BDSPage;